import React, { useState, useEffect, useCallback } from "react";
import {
  Users, Activity, RefreshCw, ExternalLink,
  TrendingUp, AlertTriangle, Siren, Trash2, PauseCircle, PlayCircle,
  Phone, Mail, Globe, Check, Square, CheckSquare,
  Camera, X, Search, MoreVertical, ArrowLeft, Bot, DollarSign,
} from "lucide-react";
import { authFetch, API } from "../utils/api";
import { toast } from "sonner";

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("es-UY", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso);
  const m = Math.floor(diff / 60000);
  if (m < 2) return "Hace un momento";
  if (m < 60) return `Hace ${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Hace ${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `Hace ${days}d`;
  return new Date(iso).toLocaleDateString("es-UY", { day: "numeric", month: "short" });
}

function fmtPrice(amt, cur) {
  if (!amt) return "N/D";
  const sym = cur === "USD" ? "U$S" : cur === "UYU" ? "$" : (cur || "$");
  return `${sym} ${Number(amt).toLocaleString("es-UY", { minimumFractionDigits: 0 })}`;
}

function getInactivityStatus(client) {
  if (client.inactivity_tag === "churn_risk") return "churn_risk";
  if (client.inactivity_tag === "at_risk") return "at_risk";
  const lastActive = client.last_dashboard_access || client.last_activity || client.created_at;
  if (!lastActive) return null;
  const daysAgo = (Date.now() - new Date(lastActive)) / 86400000;
  if (daysAgo >= 30) return "churn_risk";
  if (daysAgo >= 15) return "at_risk";
  return null;
}

/* ── Sub-components ──────────────────────────────────────────────────────── */

/**
 * Iter88 — Per-client AI Editor Agent v2 cost ledger.
 * Pulls from `/api/admin/client-accounts/{token}/agent-ledger` and shows
 * raw cost vs billed amount per edit so we can monitor margin & abuse.
 * Operator-only — clients NEVER see this.
 */
function AgentLedgerSection({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    authFetch(`${API}/admin/client-accounts/${token}/agent-ledger?limit=50`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-2.5 py-2 text-[11px] text-gray-500">
        <RefreshCw size={11} className="animate-spin" /> Cargando costos del agente…
      </div>
    );
  }
  if (!data || !data.totals?.edits) {
    return (
      <div className="px-2.5 py-3 text-[11px] text-gray-600 italic" data-testid="agent-ledger-empty">
        Sin uso del agente IA todavía
      </div>
    );
  }

  const t = data.totals;
  const margin = t.margin_usd >= 0 ? "text-emerald-400" : "text-red-400";

  return (
    <div data-testid="agent-ledger-section">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-purple-500/[0.06] border border-purple-500/20 rounded-xl hover:bg-purple-500/[0.10] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Bot size={13} className="text-purple-400" />
          <span className="text-[11px] font-black text-purple-300 uppercase tracking-wider">Costos Agente IA</span>
          <span className="text-[10px] font-bold text-gray-400">· {t.edits} ediciones</span>
        </div>
        <div className="flex items-center gap-3 text-[10.5px] font-bold">
          <span className="text-gray-400">Costo: <span className="text-gray-200">${t.raw_cost_usd.toFixed(3)}</span></span>
          <span className="text-gray-400">Cobrado: <span className="text-emerald-300">${t.billed_usd.toFixed(3)}</span></span>
          <span className={margin}>Margen: ${t.margin_usd.toFixed(3)}</span>
        </div>
      </button>
      {open && (
        <div className="mt-2 max-h-72 overflow-y-auto space-y-1.5 px-1" data-testid="agent-ledger-rows">
          {data.rows.map((r, i) => (
            <div
              key={r.id || i}
              className="flex items-start gap-2 py-2 px-2.5 rounded-lg bg-white/[0.03] border border-white/[0.05]"
            >
              <div className="flex-shrink-0 mt-0.5">
                <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded ${
                  r.applied
                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                    : r.stage === "plan"
                      ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                      : "bg-gray-500/20 text-gray-400 border border-gray-500/30"
                }`}>
                  {r.applied ? "Aplicado" : r.stage === "plan" ? "Plan" : "Sin cambios"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-gray-300 leading-snug truncate">{r.instruction}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[9.5px] text-gray-500 font-mono">
                  <span>{r.input_tokens} in · {r.output_tokens} out</span>
                  <span className="text-gray-400">${(r.raw_cost_usd || 0).toFixed(4)} → <span className="text-emerald-400">${(r.billed_usd || 0).toFixed(4)}</span></span>
                  <span className="text-[#FFC107]">{r.credits_billed} créd</span>
                  {r.error && <span className="text-red-400">· {r.error}</span>}
                </div>
                <p className="text-[9px] text-gray-600 mt-0.5">{fmtDate(r.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────────────────── */

function InactivityTag({ status }) {
  if (status === "churn_risk") {
    return (
      <span data-testid="tag-alerta-baja" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-red-500/20 text-red-400 border border-red-500/40 animate-pulse">
        <Siren size={8} /> Alerta Baja
      </span>
    );
  }
  if (status === "at_risk") {
    return (
      <span data-testid="tag-at-risk" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-500/20 text-amber-400 border border-amber-500/40">
        <AlertTriangle size={8} /> 15d inactivo
      </span>
    );
  }
  return null;
}

const EVENT_COLORS = {
  login:               "bg-blue-500/20 text-blue-400 border-blue-500/20",
  chat_edit:           "bg-purple-500/20 text-purple-400 border-purple-500/20",
  chat_accepted:       "bg-green-500/20 text-green-400 border-green-500/20",
  site_edit:           "bg-purple-500/20 text-purple-400 border-purple-500/20",
  domain_configured:   "bg-[#FFC107]/20 text-[#FFC107] border-[#FFC107]/20",
  referral_credit:     "bg-emerald-500/20 text-emerald-400 border-emerald-500/20",
  subscription_portal: "bg-gray-500/20 text-gray-400 border-gray-500/20",
  default:             "bg-gray-500/20 text-gray-400 border-gray-500/20",
};

const EVENT_LABELS = {
  login:               "Login",
  chat_edit:           "Edición IA",
  chat_accepted:       "Cambio aprobado",
  site_edit:           "Sitio editado",
  domain_configured:   "Dominio",
  referral_credit:     "Referido",
  subscription_portal: "Suscripción",
};

function ActivityBadge({ type }) {
  const cls = EVENT_COLORS[type] || EVENT_COLORS.default;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${cls}`}>
      {EVENT_LABELS[type] || type}
    </span>
  );
}

/* ── ClientRow ───────────────────────────────────────────────────────────── */

function ClientRow({ client, onSelect, selected, checked, onCheck }) {
  const aiUsed  = client.ai_mods_used_this_month || 0;
  const aiTotal = client.ai_mods_free_monthly || 10;
  const aiPct   = Math.min(100, (aiUsed / aiTotal) * 100);
  const inactivity = getInactivityStatus(client);
  const isChurn    = inactivity === "churn_risk";
  const isAtRisk   = inactivity === "at_risk";
  const initial    = (client.name || "?")[0].toUpperCase();

  return (
    <div
      data-testid={`client-row-${client.token?.slice(0, 8)}`}
      onClick={() => onSelect(client)}
      className={`
        flex items-center gap-3 px-4 py-3.5 border-b cursor-pointer transition-all duration-150
        ${isChurn   ? "border-b-red-500/20 bg-red-500/[0.04] hover:bg-red-500/[0.08] border-l-2 border-l-red-500" : ""}
        ${isAtRisk && !isChurn ? "border-b-amber-500/20 bg-amber-500/[0.03] hover:bg-amber-500/[0.06] border-l-2 border-l-amber-500" : ""}
        ${!inactivity && !selected ? "border-b-white/[0.04] hover:bg-white/[0.03]" : ""}
        ${selected && !inactivity ? "bg-[#FFC107]/[0.05] border-l-2 border-l-[#FFC107] border-b-white/[0.04]" : ""}
        ${client.site_paused ? "opacity-50" : ""}
      `}
    >
      {/* Checkbox */}
      <button
        onClick={e => { e.stopPropagation(); onCheck(client.token); }}
        className="flex-shrink-0 text-gray-600 hover:text-[#FFC107] transition-colors"
      >
        {checked
          ? <CheckSquare size={14} className="text-[#FFC107]" />
          : <Square size={14} />}
      </button>

      {/* Avatar */}
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-black
        ${isChurn  ? "bg-red-500/20 text-red-300 border border-red-500/30"
        : isAtRisk ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
        : "bg-gradient-to-br from-[#FFC107]/20 to-[#FF4F00]/20 text-white border border-white/[0.08]"}`}
      >
        {initial}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-white truncate leading-tight">{client.name || "Sin nombre"}</span>
          {client.site_paused && (
            <span className="text-[9px] bg-gray-700/60 text-gray-400 px-1.5 py-0.5 rounded-full font-bold">Pausado</span>
          )}
          {inactivity && <InactivityTag status={inactivity} />}
        </div>
        <p className="text-xs text-gray-500 truncate">{client.email || "—"}</p>
      </div>

      {/* AI bar — hidden on mobile */}
      <div className="hidden sm:block w-20 flex-shrink-0">
        <p className="text-[10px] text-gray-600 mb-1">IA {aiUsed}/{aiTotal}</p>
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${aiPct}%`, background: aiPct >= 80 ? "#ef4444" : "#FFC107" }}
          />
        </div>
      </div>

      {/* Last active */}
      <div className="hidden lg:block text-right flex-shrink-0">
        <p className={`text-xs font-medium ${isChurn ? "text-red-400" : isAtRisk ? "text-amber-400" : "text-gray-500"}`}>
          {fmtRelative(client.last_dashboard_access || client.last_activity)}
        </p>
      </div>
    </div>
  );
}

/* ── ActivityPanel ───────────────────────────────────────────────────────── */

function ActivityPanel({ client, logs, loading, onPause, onResume, onDelete, onSnapshot, snapshotLoading, onBack }) {
  const inactivity = client ? getInactivityStatus(client) : null;

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6 py-20">
        <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mb-4">
          <Activity size={24} className="text-gray-600" />
        </div>
        <p className="text-sm font-semibold text-gray-500">Seleccioná un cliente</p>
        <p className="text-xs text-gray-600 mt-1">para ver su detalle y actividad</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Mobile back button */}
      {onBack && (
        <button
          data-testid="accounts-back-btn"
          onClick={onBack}
          className="md:hidden flex items-center gap-1.5 px-5 pt-3 pb-1 text-xs text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={13} /> Volver a la lista
        </button>
      )}
      {/* Client header */}
      <div className="px-5 py-4 border-b border-white/[0.05] flex-shrink-0">
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 text-lg font-black
            ${inactivity === "churn_risk" ? "bg-red-500/20 text-red-300 border border-red-500/30"
            : inactivity === "at_risk"   ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
            : "bg-gradient-to-br from-[#FFC107]/20 to-[#FF4F00]/20 text-white border border-white/[0.08]"}`}
          >
            {(client.name || "?")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-black text-white">{client.name}</p>
              {client.site_paused && (
                <span className="text-[9px] bg-orange-500/20 text-orange-400 border border-orange-500/30 px-1.5 py-0.5 rounded-full font-bold">Pausado</span>
              )}
              {inactivity && <InactivityTag status={inactivity} />}
            </div>
            <p className="text-xs text-gray-500">{client.email || "—"}</p>
          </div>
        </div>

        {/* Contact details */}
        <div className="space-y-1.5 mb-4">
          {client.phone && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Phone size={10} className="text-gray-600 flex-shrink-0" />
              <span>{client.phone}</span>
            </div>
          )}
          {(client.site_slug || client.custom_domain || client.site_preview_url) && (
            <div className="flex items-center gap-2 text-xs">
              <Globe size={10} className="text-gray-600 flex-shrink-0" />
              <a
                href={client.custom_domain ? `https://${client.custom_domain}` : (client.site_preview_url || `/pv/${client.site_slug}`)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FFC107] hover:underline truncate flex items-center gap-1"
                onClick={e => e.stopPropagation()}
              >
                {client.custom_domain || (client.site_preview_url || `preview/${client.site_slug}`).replace(/^https?:\/\//, "")}
                <ExternalLink size={9} />
              </a>
            </div>
          )}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            { label: "Ediciones IA", value: `${client.ai_mods_used_this_month || 0}/${client.ai_mods_free_monthly || 10}`, color: "text-white" },
            { label: "Plan", value: (client.plan || "trial").toUpperCase(), color: "text-[#FFC107]" },
            { label: "Pagado", value: fmtPrice(client.subscription_amount, client.subscription_currency), color: "text-green-400" },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white/[0.03] border border-white/[0.05] rounded-xl p-2.5">
              <p className="text-[10px] text-gray-500 mb-1 truncate">{label}</p>
              <p className={`text-sm font-black ${color} truncate`}>{value}</p>
            </div>
          ))}
        </div>

        {client.paid_at && (
          <p className="text-[11px] text-gray-600 mb-3 flex items-center gap-1.5">
            <Check size={10} className="text-green-500" />
            Activo desde {fmtDate(client.paid_at)}
          </p>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onSnapshot(client.token)}
            disabled={snapshotLoading}
            data-testid="snapshot-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#FFC107]/10 border border-[#FFC107]/30 text-[#FFC107] rounded-lg font-bold hover:bg-[#FFC107]/20 disabled:opacity-50 transition-colors"
          >
            <Camera size={11} /> {snapshotLoading ? "..." : "Snapshot"}
          </button>
          {client.site_paused ? (
            <button
              onClick={() => onResume(client.token)}
              data-testid="resume-btn"
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-green-500/10 border border-green-500/30 text-green-400 rounded-lg font-bold hover:bg-green-500/20 transition-colors"
            >
              <PlayCircle size={11} /> Reactivar
            </button>
          ) : (
            <button
              onClick={() => onPause(client.token)}
              data-testid="pause-btn"
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-orange-500/10 border border-orange-500/30 text-orange-400 rounded-lg font-bold hover:bg-orange-500/20 transition-colors"
            >
              <PauseCircle size={11} /> Pausar
            </button>
          )}
          <button
            onClick={() => onDelete(client.token)}
            data-testid="delete-account-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg font-bold hover:bg-red-500/20 transition-colors"
          >
            <Trash2 size={11} /> Eliminar
          </button>
        </div>
      </div>

      {/* Activity log */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {/* Iter88 — Agent v2 cost ledger (operator-only) */}
        <div className="mb-5">
          <AgentLedgerSection token={client.token} />
        </div>

        <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Log de Actividad</p>
        {loading ? (
          <div className="flex justify-center py-10">
            <RefreshCw size={16} className="text-gray-600 animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-xs text-gray-600 text-center py-10">Sin actividad registrada</p>
        ) : (
          <div className="space-y-1.5">
            {logs.map(log => (
              <div
                key={log.id}
                className={`flex gap-3 py-2.5 px-2.5 rounded-xl border border-transparent transition-colors hover:bg-white/[0.02]
                  ${log.type === "snapshot" ? "bg-[#FFC107]/[0.03] border-[#FFC107]/10" : ""}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {log.type === "snapshot"
                    ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#FFC107] bg-[#FFC107]/10 border border-[#FFC107]/20 px-2 py-0.5 rounded-full"><Camera size={9} /> Snapshot</span>
                    : <ActivityBadge type={log.type} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-300 leading-relaxed whitespace-pre-line">{log.description}</p>
                  <p className="text-[10px] text-gray-600 mt-0.5">{fmtDate(log.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */

export default function AccountsPage() {
  const [clients,         setClients]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [selectedClient,  setSelectedClient]  = useState(null);
  const [activityLogs,    setActivityLogs]    = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [search,          setSearch]          = useState("");
  const [checked,         setChecked]         = useState(new Set());
  const [bulkLoading,     setBulkLoading]     = useState(false);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await authFetch(`${API}/admin/accounts`).then(r => r.json());
      if (Array.isArray(data)) setClients(data);
    } catch { toast.error("Error al cargar cuentas"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const selectClient = async (client) => {
    setSelectedClient(client);
    setActivityLoading(true);
    try {
      const data = await authFetch(`${API}/admin/accounts/${client.token}/activity`).then(r => r.json());
      setActivityLogs(Array.isArray(data) ? data : []);
    } catch { setActivityLogs([]); }
    finally { setActivityLoading(false); }
  };

  const handlePause = async (token) => {
    const res = await authFetch(`${API}/admin/accounts/${token}/pause`, { method: "POST" });
    const d = await res.json();
    if (d.ok) { toast.success("Sitio pausado"); loadClients(); setSelectedClient(prev => prev?.token === token ? { ...prev, site_paused: true } : prev); }
    else toast.error("Error al pausar");
  };

  const handleResume = async (token) => {
    const res = await authFetch(`${API}/admin/accounts/${token}/resume`, { method: "POST" });
    const d = await res.json();
    if (d.ok) { toast.success("Sitio reactivado"); loadClients(); setSelectedClient(prev => prev?.token === token ? { ...prev, site_paused: false } : prev); }
    else toast.error("Error al reactivar");
  };

  const handleDelete = async (token) => {
    if (!window.confirm("¿Eliminar permanentemente esta cuenta? Esta acción no se puede deshacer.")) return;
    const res = await authFetch(`${API}/admin/accounts/${token}`, { method: "DELETE" });
    const d = await res.json();
    if (d.ok) { toast.success("Cuenta eliminada"); setSelectedClient(null); loadClients(); }
    else toast.error("Error al eliminar");
  };

  const handleSnapshot = async (token) => {
    setSnapshotLoading(true);
    try {
      const res = await authFetch(`${API}/admin/accounts/${token}/snapshot`, { method: "POST" });
      const d = await res.json();
      if (d.id) {
        toast.success("Snapshot guardado en el log");
        const logs = await authFetch(`${API}/admin/accounts/${token}/activity`).then(r => r.json());
        setActivityLogs(Array.isArray(logs) ? logs : []);
      } else toast.error("Error al generar snapshot");
    } catch { toast.error("Error"); }
    finally { setSnapshotLoading(false); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`¿Eliminar ${checked.size} cuenta(s)?`)) return;
    setBulkLoading(true);
    try {
      const res = await authFetch(`${API}/admin/accounts/bulk`, {
        method: "DELETE",
        body: JSON.stringify({ tokens: Array.from(checked) }),
      });
      const d = await res.json();
      if (d.ok) { toast.success(`${d.deleted} cuenta(s) eliminadas`); setChecked(new Set()); setSelectedClient(null); loadClients(); }
      else toast.error("Error al eliminar");
    } catch { toast.error("Error"); }
    finally { setBulkLoading(false); }
  };

  const toggleCheck = (token) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(token)) next.delete(token); else next.add(token);
    return next;
  });

  const toggleAll = () => {
    if (checked.size === filtered.length) setChecked(new Set());
    else setChecked(new Set(filtered.map(c => c.token)));
  };

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    return !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.business_name?.toLowerCase().includes(q);
  });

  const activeCount    = clients.filter(c => { const la = c.last_dashboard_access || c.last_activity; return la && (Date.now() - new Date(la)) < 7 * 86400000; }).length;
  const atRiskCount   = clients.filter(c => getInactivityStatus(c) === "at_risk").length;
  const churnCount    = clients.filter(c => getInactivityStatus(c) === "churn_risk").length;

  return (
    <div className="h-full flex flex-col" style={{ background: "var(--ds-surface)", fontFamily: "'Inter', sans-serif" }}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-4"
        style={{ background: "rgba(14,14,14,0.9)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Administración</p>
          <h1 className="text-xl sm:text-2xl font-black tracking-tight text-white truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Cuentas de Clientes
          </h1>
        </div>

        <div className="flex items-center gap-3">
          {/* Stats pills */}
          <div className="hidden sm:flex items-center gap-2">
            {churnCount > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-500/15 text-red-400 border border-red-500/30 animate-pulse">
                <Siren size={11} /> {churnCount} Alerta
              </span>
            )}
            {atRiskCount > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30">
                <AlertTriangle size={11} /> {atRiskCount} En riesgo
              </span>
            )}
          </div>
          <button
            onClick={loadClients}
            data-testid="refresh-accounts-btn"
            className="p-2 rounded-xl transition-all hover:scale-105 active:scale-95"
            style={{ background: "var(--ds-surface-high)", border: "1px solid var(--ds-ghost-border)" }}
          >
            <RefreshCw size={15} className={`text-gray-400 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* ── Stat cards ─────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 grid grid-cols-2 sm:grid-cols-4 gap-px border-b border-white/[0.05]" style={{ background: "var(--ds-surface-low)" }}>
        {[
          { label: "Total",    value: clients.length, color: "#FFC107", icon: Users },
          { label: "Activos",  value: activeCount,    color: "#34D399", icon: TrendingUp },
          { label: "En riesgo",value: atRiskCount,    color: "#f59e0b", icon: AlertTriangle },
          { label: "Alerta",   value: churnCount,     color: "#ef4444", icon: Siren },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="flex flex-col items-center py-3.5 bg-[#0e0e0e]">
            <p className="text-xl font-black" style={{ color }}>{value}</p>
            <p className="text-[10px] text-gray-500 font-semibold mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Main content — two columns ──────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden min-h-0">

        {/* LEFT — Client list */}
        <div
          className={`flex flex-col w-full md:w-[52%] lg:w-[45%] xl:w-[40%] flex-shrink-0 border-r border-white/[0.05] overflow-hidden ${
            selectedClient ? "hidden md:flex" : "flex"
          }`}
        >

          {/* Search */}
          <div className="flex-shrink-0 px-4 py-3 border-b border-white/[0.04]">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
              <input
                data-testid="accounts-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar cliente, email..."
                className="w-full pl-8 pr-3 py-2 text-sm rounded-xl outline-none transition-all"
                style={{ background: "var(--ds-surface-card)", border: "1px solid rgba(255,255,255,0.07)", color: "var(--ds-text)" }}
              />
            </div>
          </div>

          {/* Bulk action bar */}
          {checked.size > 0 && (
            <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 border-b border-red-500/20 bg-red-500/[0.06]">
              <span className="text-xs text-red-400 font-bold flex-1">{checked.size} seleccionado(s)</span>
              <button
                onClick={handleBulkDelete}
                disabled={bulkLoading}
                data-testid="bulk-delete-accounts-btn"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-500 text-white rounded-lg font-bold hover:bg-red-600 disabled:opacity-60 transition-colors"
              >
                <Trash2 size={11} /> {bulkLoading ? "..." : "Eliminar"}
              </button>
              <button onClick={() => setChecked(new Set())} className="p-1.5 text-gray-500 hover:text-white rounded-lg">
                <X size={13} />
              </button>
            </div>
          )}

          {/* Select all */}
          {filtered.length > 0 && (
            <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 border-b border-white/[0.03]" style={{ background: "rgba(255,255,255,0.01)" }}>
              <button onClick={toggleAll} className="flex items-center gap-2 text-xs text-gray-500 hover:text-white transition-colors">
                {checked.size === filtered.length && filtered.length > 0
                  ? <CheckSquare size={12} className="text-[#FFC107]" />
                  : <Square size={12} />}
                {checked.size === filtered.length && filtered.length > 0 ? "Deseleccionar todos" : "Seleccionar todos"}
              </button>
            </div>
          )}

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw size={20} className="text-gray-600 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-12 h-12 rounded-2xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mb-3">
                  <Users size={20} className="text-gray-600" />
                </div>
                <p className="text-sm text-gray-500">{search ? "Sin resultados" : "Aún no hay clientes"}</p>
              </div>
            ) : (
              filtered.map(client => (
                <ClientRow
                  key={client.token}
                  client={client}
                  onSelect={selectClient}
                  selected={selectedClient?.token === client.token}
                  checked={checked.has(client.token)}
                  onCheck={toggleCheck}
                />
              ))
            )}
          </div>
        </div>

        {/* RIGHT — Activity panel */}
        <div
          className={`${selectedClient ? "flex" : "hidden md:flex"} flex-col flex-1 overflow-hidden`}
          style={{ background: "var(--ds-surface-low)" }}
        >
          <ActivityPanel
            client={selectedClient}
            logs={activityLogs}
            loading={activityLoading}
            onPause={handlePause}
            onResume={handleResume}
            onDelete={handleDelete}
            onSnapshot={handleSnapshot}
            snapshotLoading={snapshotLoading}
            onBack={() => setSelectedClient(null)}
          />
        </div>
      </div>
    </div>
  );
}
